import { useEffect, useState, PropsWithChildren, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

// Track redirect attempts to prevent loops
const MAX_REDIRECT_ATTEMPTS = 3;
const REDIRECT_STORAGE_KEY = 'auth_redirect_attempts';

export default function RequireAuth({ children }: PropsWithChildren) {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  useEffect(() => {
    let mounted = true;

    console.log('[RequireAuth] Initializing for path:', location.pathname);

    // Check redirect loop
    const checkRedirectLoop = () => {
      const attempts = parseInt(sessionStorage.getItem(REDIRECT_STORAGE_KEY) || '0');
      if (attempts >= MAX_REDIRECT_ATTEMPTS) {
        console.error('[RequireAuth] Redirect loop detected, clearing auth state');
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        localStorage.clear();
        return true;
      }
      sessionStorage.setItem(REDIRECT_STORAGE_KEY, String(attempts + 1));
      return false;
    };

    const performRedirect = () => {
      if (hasRedirected.current) return;
      
      if (checkRedirectLoop()) {
        // Loop detected - show error
        setChecking(false);
        return;
      }

      hasRedirected.current = true;
      const dest = `/auth?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`;
      console.log('[RequireAuth] Redirecting to:', dest);
      navigate(dest, { replace: true });
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      
      console.log('[RequireAuth] Auth event:', event, 'Has session:', !!newSession);
      
      setSession(newSession);
      setAuthed(!!newSession);
      
      // Only redirect if no session and checking is complete
      if (!newSession && !hasRedirected.current) {
        // Clear redirect counter on successful auth change
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        performRedirect();
      } else if (newSession) {
        // Clear redirect counter on successful auth
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
      }
    });

    // THEN check for existing session
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      
      const hasSession = !!data.session;
      console.log('[RequireAuth] Session check:', hasSession);
      
      setSession(data.session);
      setAuthed(hasSession);
      setChecking(false);
      
      // Only redirect if no session
      if (!hasSession) {
        performRedirect();
      } else {
        // Clear redirect counter on successful session
        sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
      }
    };
    
    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname, location.search, location.hash]);

  if (checking) {
    return (
      <div className="w-full h-[60vh] flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  return <>{authed && children}</>;
}
